/**
 * useDetailsData — a única fonte de dados do painel de detalhes da conversa.
 *
 * O painel abre em TODA conversa. Se ele buscasse lead, cliente, mídia e
 * retornos de uma vez, cada abertura viraria cinco requisições. Aqui cada camada
 * só busca quando é a camada aberta — é a regra de "condicionar fetch ao estado
 * de abertura" do projeto, aplicada por CAMADA em vez de por seção de acordeão.
 *
 * Duas exceções deliberadas, ambas na raiz: as contagens de mídia e o lead. As
 * linhas do índice mostram um resumo ("10 arquivos", "Proposta · R$ 486.000,00")
 * e sem esses dois o índice ficaria com resumo vazio justamente na tela que
 * existe para decidir onde entrar. As duas queries são leves e compartilham a
 * `queryKey` com o cabeçalho do chat, então na prática vêm do cache.
 */
import { useMemo } from 'react';
import type { ContactResponse, ConversationResponse, LeadResponse } from '@troia-v3/types';

import { useFollowUps } from '@/modules/calendar/hooks';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { useLeadsByContact } from '@/modules/crm/leads/hooks';

import { useContact, useContactTags, useConversation, useConversationMediaSummary } from '../../hooks';

export type DetailsLayerId =
  | 'root'
  | 'contato'
  | 'empresa'
  | 'negocio'
  | 'membros'
  | 'arquivos'
  | 'retornos';

export interface DetailsCompany {
  id: string;
  name: string;
  type: 'PF' | 'PJ';
  document?: string;
}

export interface DetailsMediaCounts {
  total: number;
  image: number;
  video: number;
  audio: number;
  document: number;
}

export interface DetailsData {
  conversation: ConversationResponse | undefined;
  isLoading: boolean;
  isGroup: boolean;
  contactId: string | undefined;
  contact: ContactResponse | undefined;
  tags: string[];
  /** Lead ATIVO do contato. Ativo = pendente, nunca ganho nem perdido. */
  lead: LeadResponse | undefined;
  /** Empresa vinculada ao contato, quando existe. */
  company: DetailsCompany | undefined;
  /** Nome da empresa mesmo quando o dropdown ainda não respondeu. */
  companyName: string | undefined;
  mediaCounts: DetailsMediaCounts;
  followUps: ReturnType<typeof useFollowUps>['followUps'];
  isLoadingFollowUps: boolean;
  refetchFollowUps: ReturnType<typeof useFollowUps>['refetch'];
}

const SEM_MIDIA: DetailsMediaCounts = { total: 0, image: 0, video: 0, audio: 0, document: 0 };

/**
 * "Ativo" = pendente. Mesma definição do `ConversationStageSelector` do
 * cabeçalho — se as duas divergirem, o cabeçalho e o painel mostram etapas
 * diferentes para o mesmo contato.
 */
const isActiveLead = (l: LeadResponse): boolean =>
  l.businessStatus !== 'won' && l.businessStatus !== 'lost';

export function useDetailsData(conversationId: string, layer: DetailsLayerId): DetailsData {
  const { data: conversation, isLoading } = useConversation(conversationId);

  const isGroup = conversation?.conversationType === 'group';
  // Grupo não tem contato único: nem lead, nem documento, nem endereço.
  const contactId = isGroup ? undefined : (conversation?.contactId ?? conversation?.contact?.id);

  // As etiquetas aparecem no topo, que nunca sai da tela — únicas que carregam
  // sempre. Mesma `queryKey` do cabeçalho (`['contact', id, 'tags']`).
  const { data: tags } = useContactTags(contactId);

  const precisaContagemDeMidia = layer === 'root' || layer === 'arquivos';
  const { data: mediaSummary } = useConversationMediaSummary(
    precisaContagemDeMidia ? conversationId : undefined,
  );

  const { data: contact } = useContact(layer === 'contato' ? contactId : undefined);

  const precisaLead = layer === 'root' || layer === 'negocio';
  const { data: leads } = useLeadsByContact(precisaLead ? contactId : undefined);
  const lead = useMemo(() => (leads ?? []).find(isActiveLead), [leads]);

  // O documento (CPF/CNPJ) vem da rota DROPDOWN de clientes, não do CRUD do
  // módulo: consumir `/customers/:id` daqui violaria a fronteira entre módulos.
  // O dropdown já devolve `document` no item.
  const customerId = conversation?.contact?.customerId;
  const { data: customers } = useCustomersDropdown({
    enabled: layer === 'empresa' && !!customerId,
  });
  const company = useMemo<DetailsCompany | undefined>(() => {
    if (!customerId) return undefined;
    const achado = (customers ?? []).find((c) => c.id === customerId);
    if (!achado) return undefined;
    return achado.document === undefined
      ? { id: achado.id, name: achado.name, type: achado.type }
      : { id: achado.id, name: achado.name, type: achado.type, document: achado.document };
  }, [customers, customerId]);

  const {
    followUps,
    isLoading: isLoadingFollowUps,
    refetch: refetchFollowUps,
  } = useFollowUps(layer === 'retornos' ? contactId : undefined);

  return {
    conversation,
    isLoading,
    isGroup: isGroup === true,
    contactId,
    contact: contact ?? undefined,
    tags: tags ?? [],
    lead,
    company,
    // O nome do cliente já vem resolvido na própria conversa. Ele é o que
    // permite a camada Empresa existir mesmo antes (ou apesar) do dropdown —
    // sem isso o índice levaria a uma tela vazia.
    companyName: conversation?.contact?.customer?.name,
    mediaCounts: mediaSummary?.counts ?? SEM_MIDIA,
    followUps,
    isLoadingFollowUps,
    refetchFollowUps,
  };
}
